import i18next from 'i18next';
import { toast } from 'sonner';
import { useChangeRequestStore } from '../../store/change-request.store.js';
import { useTenantStore } from '../../store/tenant.store.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class ChangeRequestIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    async fetchAll(page = 1, pageSize = 10, search = '', sortBy = '', sortOrder = 'desc', columnFilters = {}) {
        const store = useChangeRequestStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;
        store.setLoading(true);
        store.clearError();
        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }
        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) acc[`filter_${key}`] = value;
                    return acc;
                }, {}),
            });
            const { data, pagination, uniqueValues } = response;
            store.setItems(data || []);
            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page,
                    totalPages: pagination.totalPages,
                    totalItems: pagination.total,
                    itemsPerPage: pagination.pageSize,
                });
            }
            if (uniqueValues) store.setUniqueValues(uniqueValues);
            return { success: true };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotLoadList', { defaultValue: 'Could not load list' });
            store.setError(msg);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchById(id) {
        const store = useChangeRequestStore.getState();
        store.beginDetailFetch(id);
        try {
            const response = await this.service.getById(id);
            const data = response.data;
            if (useChangeRequestStore.getState().detailRequestId !== String(id)) {
                return { success: true, data };
            }
            store.setDetailItem(data);
            return { success: true, data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotLoadRecord', { defaultValue: 'Could not load record' });
            if (useChangeRequestStore.getState().detailRequestId === String(id)) {
                store.setDetailItem(null);
                store.setDetailError(msg);
                toast.error(msg);
            }
            return { success: false, error: msg };
        } finally {
            if (useChangeRequestStore.getState().detailRequestId === String(id)) {
                store.setDetailLoading(false);
            }
        }
    }

    applyMutationResult(id, next) {
        const store = useChangeRequestStore.getState();
        if (next && String(store.detailItem?._id) === String(id)) {
            store.setDetailItem(next);
        }
        if (store.items?.length && next) {
            store.setItems(store.items.map((row) => (
                String(row._id) === String(id) ? { ...row, ...next } : row
            )));
        }
    }

    async create(data) {
        const store = useChangeRequestStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.create(data);
            toast.success(i18next.t('integrationToasts:recordCreated', { defaultValue: 'Record created' }));
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotCreateRecord', { defaultValue: 'Could not create record' });
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async update(id, data) {
        try {
            const response = await this.service.update(id, data);
            toast.success(i18next.t('integrationToasts:recordUpdated', { defaultValue: 'Record updated' }));
            this.applyMutationResult(id, response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotUpdateRecord', { defaultValue: 'Could not update record' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async delete(id) {
        const store = useChangeRequestStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.delete(id);
            toast.success(i18next.t('integrationToasts:recordDeleted', { defaultValue: 'Record deleted' }));
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotDeleteRecord', { defaultValue: 'Could not delete record' });
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async submitForApproval(id) {
        const store = useChangeRequestStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.submitForApproval(id, {});
            toast.success(i18next.t('integrationToasts:submittedForApproval', { defaultValue: 'Submitted for approval' }));
            this.applyMutationResult(id, response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotStartApproval', { defaultValue: 'Could not start approval' });
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async approve(id, comments = '') {
        const store = useChangeRequestStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.approve(id, { comments });
            toast.success(i18next.t('integrationToasts:approved', { defaultValue: 'Approved' }));
            this.applyMutationResult(id, response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotProcessApproval', { defaultValue: 'Could not process approval' });
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async reject(id, comments = '') {
        const store = useChangeRequestStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.reject(id, { comments });
            toast.success(i18next.t('integrationToasts:rejected', { defaultValue: 'Rejected' }));
            this.applyMutationResult(id, response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotProcessRejection', { defaultValue: 'Could not process rejection' });
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }
}
